import * as openai from "openai";
import { APIConnectionTimeoutError } from "openai/error";
import { ChatCompletion } from "openai/resources";
import { inspect } from "util";

import pkg from "../package.json";
import { DefaultService } from "./codegen";

export type OpenPipeConfig = {
  apiKey?: string;
  baseUrl?: string;
  fallbackClient?: openai.OpenAI;
};

export type CacheSetting = "readWrite" | "readOnly" | "writeOnly";

export type OpenPipeArgs = {
  openpipe?: {
    logRequest?: boolean;
    cache?: CacheSetting;
    tags?: Record<string, string | number | boolean | null>;
    fallback?: { model: string; timeout?: number };
    criteria?: string[];
  };
};

export type OpenPipeMeta = {
  // We report your call to OpenPipe asynchronously in the background. If you
  // need to wait until the report is sent to take further action, you can await
  // this promise.
  reportingFinished: Promise<void>;
};

export type ReportFn = (...args: Parameters<DefaultService["report"]>) => Promise<void>;

// Used by the server to identify and remove the SDK tags during node entry import
export enum DefaultSdkTags {
  SDK = "$sdk",
  SDK_VERSION = "$sdk.version",
}

const defaultSdkTags: string[] = Object.values(DefaultSdkTags);

export const getTags = (
  args: OpenPipeArgs["openpipe"],
): Record<string, string | number | boolean | null> => ({
  ...args?.tags,
  [DefaultSdkTags.SDK]: "typescript",
  [DefaultSdkTags.SDK_VERSION]: pkg.version,
});

export const withTimeout = <T>(
  promise: Promise<T>,
  timeout: number,
  onTimedOut?: () => void,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    // Set up the timeout
    const timeoutId = setTimeout(() => {
      onTimedOut?.();
      reject(new APIConnectionTimeoutError({ message: "Request timed out" }));
    }, timeout);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((e) => {
        clearTimeout(timeoutId);
        reject(e);
      });
  });
};

export const inspectObject = (obj: any) => {
  console.log(inspect(obj, { colors: true, depth: null }));
};

type CriteriaResultsType = NonNullable<
  Awaited<ReturnType<DefaultService["createChatCompletion"]>>
>["choices"][number]["criteria_results"];

export type OpenPipeChatCompletion = ChatCompletion & {
  choices: (ChatCompletion["choices"][number] & {
    criteria_results?: CriteriaResultsType;
  })[];
};
